/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser, getUserType } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { GUEST, HOST } from '../../../../util/types';
import { useSelector } from 'react-redux';

const CustomLinkComponent = ({ linkConfig, user, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const userType = getUserType(user);
  if (userType == GUEST || text == 'For Hosts') {
    return null;
  }
  if (userType === HOST && text == 'Home') {
    return null;
  }
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
  } = props;
  const [showPayoutDetailsMissing, setShowPayoutDetailsMissing] = useState(false);
  const [idverifpending, setIdverifpending] = useState(false);
  const [showProfilePendingApprove, setShowProfilePendingApprove] = useState(false);
  const stripeAccountDetails = useSelector(state => state?.stripeConnectAccount?.stripeAccount);
  const isStripeAccountConnected =
    stripeAccountDetails &&
    stripeAccountDetails?.attributes?.stripeAccountData?.requirements?.currently_due?.length == 0;
  const veriffStatus = currentUser?.attributes?.profile?.metadata?.veriffStatus || {};

  const user = ensureCurrentUser(currentUser);
  // const userType = getUserType(user);
  const extraLinks = customLinks
    .filter(linkConfig => linkConfig.text) // Exclude specific link
    .map((linkConfig, index) => (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
        user={user}
      />
    ));

  if (!isAuthenticated) {
    const signup = (
      <NamedLink
        name="SignupForUserTypePage"
        params={{ userType: currentPage === 'CMSPage:hosts' ? HOST : GUEST }}
        className={css.signupLink}
      >
        <span className={css.topbarLinkLabel}>
          {currentPage === 'CMSPage:hosts' ? (
            <FormattedMessage id="TopbarDesktop.Host.signupMobile" />
          ) : (
            <FormattedMessage id="TopbarDesktop.Guest.signupMobile" />
          )}
        </span>
      </NamedLink>
    );
    const signupLink = (
      <NamedLink
        name="SignupForUserTypePage"
        params={{ userType: currentPage === 'CMSPage:hosts' ? HOST : GUEST }}
        className={css.signupLinkfff}
      >
        <span className={css.topbarLinkLabel}>
          {currentPage === 'CMSPage:hosts' ? (
            <FormattedMessage id="TopbarDesktop.Host.signupMobile" />
          ) : (
            <FormattedMessage id="TopbarDesktop.Guest.signupMobile" />
          )}
        </span>
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    // const signupOrLogin = (
    //   <span className={css.authenticationLinks}>
    //     <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
    //   </span>
    // );
    const loginLink = (
      <NamedLink name="LoginPage" className={css.loginLinkfff}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );
    const signupOrLoginLink = (
      <span className={css.authenticationLinksgt}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ signup: signupLink, login: loginLink }}
        />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreetingLogin"
              values={{ lineBreak: <br />, signupOrLogin: signupOrLoginLink }}
            />
          </div>
          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <div className={css.hostHomeLink}>
            <a href="/p/hosts">
              <FormattedMessage id="TopbarMobileMenu.forHosts" />
            </a>
          </div>
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          {signup}
          <div className={css.divider}>
            <div className={css.line}></div>
            <span className={css.text}>or</span>
            <div className={css.line}></div>
          </div>
          {login}
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const userType = user?.attributes?.profile?.publicData?.userType || null;
  const inboxTab = userType === HOST ? 'sales' : 'orders';
  const userUUID = user?.id?.uuid || null;
  return (
    <div className={css.root}>
      <div className={css.topContent}>
        <AvatarLarge className={css.avatar} user={currentUser} />
        <span className={css.greeting}>
          <FormattedMessage
            id="TopbarMobileMenu.greeting1"
            values={{ displayName: <span>{displayName}</span> }}
          />
        </span>
      </div>
      {showProfilePendingApprove && (
        <div className={css.payoutDetailsMissing}>
          <FormattedMessage id="TopbarMobileMenu.Profile.notapproved" />
        </div>
      )}
      <div className={css.content}>
        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {userType == HOST ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          ) : null}

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfilePage'))}
            name="ProfilePage"
            params={{ id: userUUID }}
          >
            <FormattedMessage id="TopbarDesktop.yourProfileLink" />
          </NamedLink>

          {/* {userType == HOST ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
          ) : null} */}

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          {userType == HOST ? (
            <a
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              href="/p/hosts"
            >
              <FormattedMessage id="TopbarMobileMenu.forHosts" />
            </a>
          ) : null}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ContactusPage'))}
            name="ContactusPage"
          >
            <FormattedMessage id="Sectionfooter.contactConcierge" />
          </NamedLink>

          {extraLinks}
          <InlineTextButton
            rootClassName={classNames(css.navigationLink, css.logoutButton)}
            onClick={onLogout}
          >
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </div>
        {/* <div className={css.customLinksWrapper}>{extraLinks}</div> */}

        {showPayoutDetailsMissing && (
          <div className={css.payoutDetailsMissing}>
            <FormattedMessage id="TopbarMobileMenu.PayoutMissing" />
          </div>
        )}
        {idverifpending && (
          <div className={css.payoutDetailsMissing}>
            <FormattedMessage id="TopbarMobileMenu.identity.notverified" />
            <NamedLink name="IdVerificationPage" className={css.clickHere}>
              <FormattedMessage id="TopbarMobileMenu.verifyidentity.linkbutton" />
            </NamedLink>
          </div>
        )}
        <div className={css.spacer} />
      </div>
      <div></div>
      {userType == HOST ? (
        <div className={css.footer}>
          {/* {console.log(currentUser?.attributes?.profile?.publicData?.ProfileListingApproved, " CHECKINGGGG")} */}
          {/* {!currentUser?.attributes?.profile?.publicData?.ProfileListingApproved ? (
            <div>
              <button
                type="button"
                onClick={() => setShowProfilePendingApprove(true)}
                className={css.createNewListingLink}
              >
                <FormattedMessage id="TopbarMobileMenu.newListingLink" />
              </button>
            </div>
          ) : !isStripeAccountConnected ? (
            <div>
              <button
                type="button"
                onClick={() => setShowPayoutDetailsMissing(true)}
                className={css.createNewListingLink}
              >
                <FormattedMessage id="TopbarMobileMenu.newListingLink" />
              </button>
            </div>
          ) :  */}
          {/* {veriffStatus !== 'approved' ? (
            <div>
              <button
                type="button"
                onClick={() => setIdverifpending(true)}
                className={css.createNewListingLink}
              >
                <FormattedMessage id="TopbarMobileMenu.newListingLink" />
              </button>
            </div>
          ) : ( */}
          {!currentUser?.attributes?.profile?.publicData?.ProfileListingApproved ? (
            <div>
              <button
                type="button"
                onClick={() => setShowProfilePendingApprove(true)}
                className={css.createNewListingLink}
              >
                <FormattedMessage id="TopbarMobileMenu.newListingLink" />
              </button>
            </div>
          ) : (
            <NamedLink className={css.createNewListingLink} name="NewListingPage">
              <FormattedMessage id="TopbarMobileMenu.newListingLink" />
            </NamedLink>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default TopbarMobileMenu;
